import React, {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
} from "react";
import debounce from "lodash.debounce";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { ContentInTooltip } from "../../../Components/CommonFunctions/CommonFunctions";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from '../../../Components/Loader';

const PharmacyGSTUpdate = () => {

    const [dosageTypes, setDosageTypes] = useState([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [stockItems, setStockItems] = useState([]);
    const [brandSearch, setBrandSearch] = useState("");
    const [genericSearch, setGenericSearch] = useState("");
    const [HSNCodeSearch, setHSNCodeSearch] = useState("");
    const [gstFilter, setGstFilter] = useState("");
    const [dosageFilter, setDosageFilter] = useState("");
    const [newGST, setNewGST] = useState("");
    const [loading, setLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }
    const errorMessage = (msg) => {
        seterrorMsg(true)
        seterrorMsgText(msg)
        setLoading(false)
    }

    const successMessage = (msg) => {
        setsuccessMsg(true)
        setsuccessMsgText(msg)
    }

    // Fetch dosage types on mount
    useEffect(() => {
        fetchDosageTypes();
        fetchGetStockList();
    }, []);

    const fetchDosageTypes = async () => {
        try {
            const response = await RestAPIService.getAll(Serviceurls.PHARMA_DOSAGE_TYPE_LIST);
            if (response.data.status === "success") {
                const data = response.data.data?.length > 0 ? response.data.data : [];
                setDosageTypes(data);
            } else {
                errorMessage(response.data?.message);
            }
        } catch (err) {
            if (err?.response?.data?.status) {
                errorMessage(err.response.data?.message);
            } else {
                errorMessage(err.message);
            }
        }
    };

    //get stock list
    const fetchGetStockList = async () => {
        try {
            setLoading(true);
            const response = await RestAPIService.getAll(Serviceurls.GET_STOCK_LIST);
            if (response.data.status === "Success") {
                const data = response.data.result?.length > 0 ? response.data.result : [];
                setStockItems(data);
                setIsCompleted(response.data.is_gst_updated_by_client);
                setLoading(false);
            }
        } catch (err) {
            if (err?.response?.data?.message) {
                errorMessage(err.response.data?.message);
            } else {
                errorMessage(err?.message);
            }
        }
    };

    const postStockItems = async (is_Completed) => {
        if (is_Completed === true) {
            setIsDialogOpen(true); 
            if (!is_Completed) return;
        } 
        try {
            let data = { updates: stockItems, is_gst_updated_by_client: is_Completed };
            const response = await RestAPIService.create(data, Serviceurls.GET_STOCK_LIST)
            if (response.data.status === "Success") {
                fetchGetStockList();
                successMessage(response.data?.message)
                setLoading(false);
            } else {
                errorMessage(response.data?.message);
            }
        } catch (err) {
            if (err?.response?.data?.message) {
                errorMessage(err.response.data?.message);
            } else {
                errorMessage(err?.message);
            }
        }
    };

    // --- Debounced GST updater (by id) ---
    const debouncedUpdateGST = useMemo(
        () =>
            debounce((id, newGST) => {
                setStockItems((prevItems) =>
                    prevItems.map((item) =>
                        item.id === id ? { ...item, revised_gst_percentage: Number(newGST) } : item
                    )
                );
            }, 300),
        []
    );

    const handleGSTChange = useCallback(
        (id, newGST) => {
            debouncedUpdateGST(id, newGST);
        },
        [debouncedUpdateGST]
    );

    // --- Filtering (memoized for performance) ---
    const filteredItems = useMemo(() => {
        const brandTerm = brandSearch.toLowerCase();
        const genericTerm = genericSearch.toLowerCase();
        const hsnTerm = HSNCodeSearch.toLowerCase();

        return stockItems.filter((item) => {
            const brandMatch = item.drug_name?.toLowerCase()?.includes(brandTerm);
            const genericMatch = item.generic_name?.toLowerCase()?.includes(genericTerm);
            const gstMatch = !gstFilter || item.gst_percentage === Number(gstFilter);
            const dosageMatch = !dosageFilter || item.dosage_type === dosageFilter;
            const HSNCode = item.hsn?.toLowerCase()?.includes(hsnTerm) 

            return brandMatch && genericMatch && gstMatch && dosageMatch && HSNCode;
        });
    }, [stockItems, brandSearch, genericSearch, gstFilter, dosageFilter, HSNCodeSearch]);

    // --- Virtualization setup ---
    const rowHeight = 45;
    const containerHeight = 400;
    const buffer = 5;

    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef(null);

    const totalHeight = filteredItems.length * rowHeight;

    const startIndex = Math.max(
        0,
        Math.floor(scrollTop / rowHeight) - buffer
    );
    const endIndex = Math.min(
        filteredItems.length,
        Math.ceil((scrollTop + containerHeight) / rowHeight) + buffer
    );

    const visibleItems = filteredItems.slice(startIndex, endIndex);

    const handleScroll = (e) => {
        setScrollTop(e.currentTarget.scrollTop);
    };

    const handleBulkGSTUpdate = () => {
        if (!newGST.trim()) return;
        const gstValue = parseFloat(newGST);

        // update only the filtered items
        setStockItems((prev) =>
            prev.map((item) =>
                filteredItems.some((f) => f.id === item.id)
                    ? { ...item, revised_gst_percentage: gstValue }
                    : item
            )
        );
        setNewGST("");
    };

    const handleCompleteClick = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleConfirmUpdate = () => {
        setLoading(true);
        postStockItems(true);
        setIsDialogOpen(false);
    };


    return (
        <div style={{ padding: "20px" }}>
            <h2>
                GST Revision for Stock ({stockItems.length} Items)
            </h2>

            {/* --- Search + Filters --- */}
            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "15px",
                }}
            >
                <input
                    type="text"
                    placeholder="Search Brand Name..."
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        width: "16vw",
                    }}
                />
                <input
                    type="text"
                    placeholder="Search Generic Name..."
                    value={genericSearch}
                    onChange={(e) => setGenericSearch(e.target.value)}
                    style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        width: "16vw",
                    }}
                />
                <select
                    value={gstFilter}
                    onChange={(e) => setGstFilter(e.target.value)}
                    style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        width: "16vw",
                    }}
                >
                    <option value="">All GST</option>
                    <option value={0}>0%</option>
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                    <option value={28}>28%</option>
                </select>

                {/* Dosage Type Filter */}
                <Autocomplete
                    options={dosageTypes}
                    style={{ width: "16vw" }}
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name
                    }
                    value={dosageFilter}
                    onChange={(event, newValue) => setDosageFilter(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Dosage Type" variant="outlined" size='small' />
                    )}
                />
                <input
                    type="text"
                    placeholder="HSN Code"
                    value={HSNCodeSearch}
                    onChange={(e) => setHSNCodeSearch(e.target.value)}
                    style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        width: "16vw",
                    }}
                />
            </div>

            {/* --- Show extra field if filter applied --- */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "15px" }}>
                <select
                    disabled={!(brandSearch || genericSearch || gstFilter || dosageFilter || HSNCodeSearch)}
                    value={newGST}
                    onChange={(e) => setNewGST(e.target.value)}
                    style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        height: "36px",
                        width: "20vw",
                    }}
                >
                    <option value="">Select Revised GST %</option>
                    <option value={0}>0%</option>
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                    <option value={28}>28%</option>
                </select>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleBulkGSTUpdate}
                    style={{ width: "20vw" }}
                    disabled={!(brandSearch || genericSearch || gstFilter || dosageFilter || HSNCodeSearch)}
                >
                    Update for All
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    style={{ width: "20vw" }}
                    onClick={() => {
                        setLoading(true);
                        postStockItems(false)
                    }}
                    disabled={isCompleted || loading}
                >
                    Save Draft
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    style={{ width: "20vw" }}
                    onClick={() => {
                        handleCompleteClick()
                    }}
                    disabled={isCompleted || loading}
                >
                    Complete
                </Button>
            </div>


            <div
                style={{
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    overflow: "hidden",
                }}
            >
                {/* Table header */}
                <div
                    style={{
                        display: "flex",
                        fontWeight: "bold",
                        borderBottom: "2px solid #ddd",
                        padding: "8px",
                        backgroundColor: "#f0f0f0",
                    }}
                >
                    <div style={{ width: "20VW" }}>Brand Name</div>
                    <div style={{ width: "20VW" }}>Generic Name</div>
                    <div style={{ width: "20VW" }}>Dosage Type</div>
                    <div style={{ width: "20VW" }}>HSN Code</div>
                    <div style={{ width: "20VW" }}>Existing GST %</div>
                    <div style={{ width: "20VW" }}>Revised GST %</div>
                </div>

                {/* Virtualized list container */}
                <div
                    ref={containerRef}
                    style={{
                        height: containerHeight,
                        overflowY: "auto",
                        position: "relative",
                    }}
                    onScroll={handleScroll}
                >
                    {/* Fake spacer */}
                    <div style={{ height: totalHeight, position: "relative" }}>
                        {visibleItems.map((item, i) => {
                            const actualIndex = startIndex + i;
                            return (
                                <div
                                    key={item.id}
                                    style={{
                                        position: "absolute",
                                        top: actualIndex * rowHeight,
                                        left: 0,
                                        right: 0,
                                        height: rowHeight,
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "4px 8px",
                                        borderBottom: "1px solid #ddd",
                                        backgroundColor:
                                            actualIndex % 2 === 0 ? "#fafafa" : "#fff",
                                    }}
                                >
                                    <div style={{ width: "20VW" }}>{ContentInTooltip(item.drug_name, 20)}</div>
                                    <div style={{ width: "20VW" }}>{ContentInTooltip(item.generic_name, 20)}</div>
                                    <div style={{ width: "20VW" }}>{item.dosage_type}</div>
                                    <div style={{ width: "20VW" }}>{item.hsn}</div>
                                    <div style={{ width: "20VW" }}>{item.gst_percentage}%</div>
                                    <div style={{ width: "20VW" }}>
                                        <select
                                            value={item.revised_gst_percentage}
                                            // disabled
                                            disabled={isCompleted}
                                            onChange={(e) =>
                                                handleGSTChange(item.id, e.target.value)
                                            }
                                            style={{
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                border: "1px solid #ccc",
                                                width: "100%",
                                            }}
                                        >
                                            <option value={0}>0%</option>
                                            <option value={5}>5%</option>
                                            <option value={12}>12%</option>
                                            <option value={18}>18%</option>
                                            <option value={28}>28%</option>
                                        </select>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {
                (errorMsg && errorMsgText) ?
                    <ToastMsg
                        severity={'error'}
                        msg={errorMsgText}
                        msgPop={msgClose.bind(this)}
                    />
                    : null
            }
            {
                (successMsg && successMsgText) ?
                    <ToastMsg
                        severity={'success'}
                        msg={successMsgText}
                        msgPop={msgClose.bind(this)}
                    />
                    : null
            }
            <Dialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
            >
                <DialogTitle>Confirm GST Update</DialogTitle>
                <DialogContent>
                    Are you sure you want to Complete GST update? Once completed, no further changes can be made.
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmUpdate} color="primary" variant="contained">
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>
            <Loader loaderOpen={loading} />
        </div>
    );
};

export default PharmacyGSTUpdate;




